import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
// @mui
import {
  Stack,
  Typography,
  Checkbox,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { styled, useTheme } from '@mui/material/styles';
// hooks
import useResponsive from '../../../../hooks/useResponsive';
import useAuth from '../../../../hooks/useAuth';
// components
import { MotionViewport } from '../../../../components/animate';
import Label from '../../../../components/Label';
// util
import axios from '../../../../utils/axios';

const Spacer = styled('div')(({ theme }) => {
  return {
    marginBottom: theme.spacing(5),
  };
});

// developer
const FIRST_OPTIONS = (smUp) => [
  { label: <Typography variant={smUp ? "body1" : "body2"}><b>처음부터 서비스를 개발</b>해야합니다.</Typography>, value: "new" },
  { label: <Typography sx={{ ...!smUp && {lineHeight: 1.125} }} variant={smUp ? "body1" : "body2"}><b>이미 개발된 서비스</b>가 있습니다.{!smUp && <br />} <Typography variant={smUp ? "body1" : "caption"} component="span">해당 서비스의 기능 개발 및 유지 보수를 담당하게 됩니다.</Typography></Typography>, value: "maintain" },
];

const SECOND_OPTIONS = (smUp) => [
  { label: <Typography variant={smUp ? "body1" : "body2"}><b>실무 위주</b>: 설계, 코딩 등 실무 개발 작업</Typography>, value: "work" },
  { label: <Typography variant={smUp ? "body1" : "body2"}><b>컨설팅 위주</b>: 구조 확인, 코드 리뷰 등 컨설팅 업무</Typography>, value: "consulting" },
];

const THIRD_OPTIONS = ["프론트엔드 개발자", "백엔드 개발자", "UX/UI 디자이너", "서비스 기획자", "프로덕트 매니저", "CTO"];

// tech lead
const ISSUE_OPTIONS = (smUp) => [
  // speed,scale,schedule,culture,bug,collaboration
  {
    label: "서비스 속도가 너무 느려요.",
    value: "speed",
  },
  {
    label: smUp ? "확장성이 없어서 새로운 기능을 추가하기 어려워요." : "새로운 기능을 추가하기 어려워요.",
    value: "scale",
  },
  {
    label: smUp ? "예상 못한 이슈로 배포 일정을 지키기 어려워요." : "배포 일정을 지키기 어려워요.",
    value: "schedule",
  },
  {
    label: smUp ? "지금 문제는 없지만, 체계적인 개발 문화를 이식하고 싶어요." : "체계적인 개발 문화를 이식하고 싶어요.",
    value: "culture",
  },
  {
    label: "버그가 많이 발생해요.",
    value: "bug",
  },
  {
    label: smUp ? "급하게 개발하느라 코드가 너무 지저분하고 협업하기 어려워요." : "급하게 개발하느라 코드가 지저분해요.",
    value: "collaboration",
  },
];

const EXPERIENCE_OPTIONS = (smUp) => [
  { label: "스택에 대한 기술적 이해도가 높은 분", value: "technical" },
  { label: "스타트업에서 고군분투한 경험이 있는 분", value: "startup" },
  { label: smUp ? "빅테크에서 체계적으로 주니어를 리드한 경험이 있는 분" : "빅테크에서 주니어를 리드한 경험이 있는 분", value: "bigtech" },
];

const STACK_OPTIONS = [
  "React",
  "Vue.Js",
  "Node.Js",
  "Java",
  "Python",
  "PHP",
  "Go",
  "IOS",
  "Android",
  "React Native",
  "Flutter",
  "AI",
];

function getStyles(name, selection, theme) {
  return {
    fontWeight:
      selection.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

export default function SetTaskForm({ jobPost, next }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = useState(false);
  const techLeadSelected = jobPost?.detail_role?.role === "Tech Lead";
  // 객관식 답변
  const [canSubmit, setCanSubmit] = useState(false);
  const [phase, setPhase] = useState(null);
  const [mainTask, setMainTask] = useState(null);
  const [collaborator, setCollaborator] = useState([]);
  // 테크리드 답변
  const [canTechleadSubmit, setCanTechleadSubmit] = useState(false);
  const [technicalIssues, setTechnicalIssues] = useState([]);
  const [experience, setExperience] = useState(null);
  const [stack, setStack] = useState([]);

  const handleChangeStack = (event) => {
    const { target: { value }, } = event;
    setStack(
      // On autofill we get a stringified value.
      (value && typeof value === 'string') ? value.split(',') : value,
    );
  };
  
  const handleError = (error) => {
    console.error(error);
    if (error.response && error.response.status === 400) {
      enqueueSnackbar(error.response.data.message, { variant: 'error' });
    } else {
      enqueueSnackbar('저장에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const setJobContext = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const jobPostId = jobPost.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/job-context/`, {
        phase,
        main_task: mainTask,
        collaborator: collaborator.join(', '),
      });
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      await next(jobPostId);
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      handleError(error);
    }
  };

  const hasSelected = (option) => {
    return collaborator.includes(option);
  };

  const selectCollaborator = (option) => {
    if (hasSelected(option)) {
      setCollaborator((prev) => {
        return prev.filter((item) => item !== option);
      });
    } else {
      setCollaborator((prev) => {
        return [...prev, option];
      });
    }
  };

  useEffect(() => {
    if (phase && mainTask) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [phase, mainTask]);

  useEffect(() => {
    if (jobPost.context_phase) {
      setPhase(jobPost.context_phase);
    }
    if (jobPost.context_main_task) {
      setMainTask(jobPost.context_main_task);
    }
    if (jobPost.context_collaborator) {
      setCollaborator(jobPost.context_collaborator.split(', '));
    }
    if (jobPost.context_technical_issues) {
      setTechnicalIssues(jobPost.context_technical_issues.split(','));
    }
    if (jobPost.context_preferred_experience) {
      setExperience(jobPost.context_preferred_experience);
    }
    if (jobPost.context_development_stack) {
      setStack(jobPost.context_development_stack.split(','));
    }
  }, [jobPost]);

  // tech lead functions
  useEffect(() => {
    if (technicalIssues.length > 0 && experience && stack.length > 0) {
      setCanTechleadSubmit(true);
    } else {
      setCanTechleadSubmit(false);
    }
  }, [technicalIssues, experience, stack]);

  const issueSelected = (option) => {
    return technicalIssues.includes(option.value);
  };

  const selectIssue = (option) => {
    if (issueSelected(option)) {
      setTechnicalIssues((prev) => {
        return prev.filter((item) => item !== option.value);
      });
    } else {
      setTechnicalIssues((prev) => {
        return [...prev, option.value];
      });
    }
  };

  const setTechleadJobContext = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const jobPostId = jobPost.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/techlead-job-context/`, {
        technical_issues: technicalIssues.join(','),
        preferred_experience: experience,
        development_stack: stack.join(','),
      });
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      await next(jobPostId);
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      handleError(error);
    }
  };

  if (techLeadSelected) {
    return (
      <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {/* # Label */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
            #원격 근무
          </Label>
          <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
            #파트타임
          </Label>
          <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
            #4주 단위
          </Label>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 , flexWrap: 'wrap', gap: smUp ? 0.9 : 0.5 }}>
        <Typography variant="h4">
          어떤 상황인지 조금 더 알려주세요.
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ ...!smUp && { color: 'text.secondary' } }}>
        작성해 주신 내용을 참고해서, 적합한 테크리드를 연결해 드립니다.
      </Typography>

      <Spacer />

      {/* issue */}
      <Typography variant="h6" gutterBottom>
        어떤 기술적인 문제를 겪고 계신가요?
      </Typography>

      <Box
        component={MotionViewport}
        sx={{
          mt: 2,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
          },
        }}
      >
        {ISSUE_OPTIONS(smUp).map((option, index) => {
          return (
            <Stack direction="row" alignItems="center"
              key={index}
              sx={{
                background: (theme) => issueSelected(option) && theme.palette.grey[200],
                '&:hover': {
                  cursor: 'pointer',
                  background: (theme) => theme.palette.grey[200],
                },
                borderRadius: 1,
                border: (theme) => issueSelected(option) ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                py: 0.5,
                px: 0.5,
              }}
              onClick={(e) => {e.stopPropagation(); selectIssue(option)}}
            >
              <Checkbox
                checked={issueSelected(option)}
                onClick={(e) => {e.stopPropagation(); selectIssue(option)}}
              />
              <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: 'break-all', fontWeight: issueSelected(option) ? "bold" : "normal" }}>
                {option.label}
              </Typography>
            </Stack>
          )
        })}
      </Box>

      <Spacer />

      {/* experience */}
      <Typography variant="h6" gutterBottom>
        선호하는 테크리드의 경험을 선택해주세요.
      </Typography>

      <RadioGroup name="techleadExperience" value={experience} onChange={(e) => setExperience(e.target.value)}>
        {EXPERIENCE_OPTIONS(smUp).map((option, index) => {
          return (
            <FormControlLabel
              sx={{
                "&:hover": {background: (theme) => theme.palette.grey[200]},
                background: (theme) => experience === option.value && theme.palette.grey[200],
                mx: 0,
                py: 1.5,
                px: 2,
                my: 1,
                border: (theme) => experience === null ? `1px solid ${theme.palette.grey[500_32]}` : experience === option.value ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                borderRadius: 1,
                alignItems: "center",
                color: experience === null ? "text.primary" : experience === option.value ? "text.primary" : "text.disabled",
              }}
              key={index}
              value={option.value}
              control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
              label={<Typography variant={smUp ? "body1" : "body2"}>{option.label}</Typography>}
            />
          )}
        )}
      </RadioGroup>

      <Spacer />

      {/* stack */}
      <Typography variant="h6" gutterBottom>
        어떤 개발 스택의 테크리드가 필요하신가요?
      </Typography>

      <Select
        fullWidth
        multiple
        displayEmpty
        value={stack}
        inputProps={{ sx: { py: 1.5 } }}
        onChange={handleChangeStack}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <Typography sx={{ py: 0.5, color: "text.disabled" }}>활용할 수 있는 서비스를 선택해주세요</Typography>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </Box>
          )
        }}
      >
        {STACK_OPTIONS.map((option, index) => (
          <MenuItem
            key={index}
            value={option}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 0.75,
              typography: 'body2',
            }}
            style={getStyles(option, stack, theme)}
          >
            {option}
          </MenuItem>
        ))}
      </Select>

      <Spacer />

      <LoadingButton
        size="large"
        variant="contained"
        sx={{ my: 6 }}
        disabled={!canTechleadSubmit}
        onClick={setTechleadJobContext}
        loading={isSubmit}
      >
        다음
      </LoadingButton>

      </>
    )
  }

  return (
    <>
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {/* # Label */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #원격 근무
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #파트타임
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #4주 단위
        </Label>
      </Stack>
    </Stack>
    <Stack direction="row" alignItems="center" sx={{ mb: 1 , flexWrap: 'wrap', gap: smUp ? 0.9 : 0.5 }}>
      <Typography variant="h4">
        어떤 상황인지 조금 더 알려주세요.
      </Typography>
    </Stack>

    <Typography variant="body2" sx={{ ...!smUp && { color: 'text.secondary' } }}>
      세부 내용을 작성해주시면, 정확하고 빠른 매칭이 가능합니다.
    </Typography>

    <Spacer />

    {/* phase */}
    <Typography variant="h6" gutterBottom>
      어떤 단계에서 개발자가 투입되나요?
    </Typography>

    <RadioGroup name="phaseRadio" value={phase} onChange={(e) => setPhase(e.target.value)}>
      {FIRST_OPTIONS(smUp).map((option, index) => {
        return (
          <FormControlLabel
            sx={{
              "&:hover": {background: (theme) => theme.palette.grey[200]},
              background: (theme) => phase === option.value && theme.palette.grey[200],
              mx: 0,
              py: 1.5,
              px: 2,
              my: 1,
              border: (theme) => phase === null ? `1px solid ${theme.palette.grey[500_32]}` : phase === option.value ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
              borderRadius: 1,
              alignItems: "center",
              color: phase === null ? "text.primary" : phase === option.value ? "text.primary" : "text.disabled",
            }}
            key={index}
            value={option.value}
            control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
            label={option.label}
          />
        )}
      )}
    </RadioGroup>

    <Spacer />

    {/* main task */}
    <Typography variant="h6" gutterBottom>
      주로 어떤 업무를 맡기실 예정인가요?
    </Typography>
    <Typography variant="body2" gutterBottom>
      두가지 모두 중요하지만, 어떤 것을 더 중요하게 생각하시는지 알려주세요. 
    </Typography>

    <RadioGroup name="mainTaskRadio" value={mainTask} onChange={(e) => setMainTask(e.target.value)}>
      {SECOND_OPTIONS(smUp).map((option, index) => {
        return (
          <FormControlLabel
            sx={{
              "&:hover": {background: (theme) => theme.palette.grey[200]},
              background: (theme) => mainTask === option.value && theme.palette.grey[200],
              mx: 0,
              py: 1.5,
              px: 2,
              my: 1,
              border: (theme) => mainTask === null ? `1px solid ${theme.palette.grey[500_32]}` : mainTask === option.value ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
              borderRadius: 1,
              alignItems: "center",
              color: mainTask === null ? "text.primary" : mainTask === option.value ? "text.primary" : "text.disabled",
            }}
            key={index}
            value={option.value}
            control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
            label={option.label}
          />
        )}
      )}
    </RadioGroup>

    <Spacer />

    <Typography variant="h6" gutterBottom>
      같이 협업할 사람이 있나요?
    </Typography>
    <Typography variant="body2" gutterBottom>
      없다면 선택하지 않으셔도 됩니다. 
    </Typography>

    <Box
      component={MotionViewport}
      sx={{
        mt: 2,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
      }}
    >
      {THIRD_OPTIONS.map((option, index) => {
        return (
          <Stack direction="row" alignItems="center"
            key={index}
            sx={{
              background: (theme) => hasSelected(option) && theme.palette.grey[200],
              '&:hover': {
                cursor: 'pointer',
                background: (theme) => theme.palette.grey[200],
              },
              borderRadius: 1,
              border: (theme) => hasSelected(option) ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
              py: 0.5,
              px: 0.5,
            }}
            onClick={(e) => {e.stopPropagation(); selectCollaborator(option)}}
          >
            <Checkbox
              checked={hasSelected(option)}
              onClick={(e) => {e.stopPropagation(); selectCollaborator(option)}}
            />
            <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: 'break-all', fontWeight: hasSelected(option) ? "bold" : "normal" }}>
              {option}
            </Typography>
          </Stack>
        )
      })}
    </Box>

    <LoadingButton
      size="large"
      variant="contained"
      sx={{ mt: smUp ? 7 : 6, mb: 4 }}
      disabled={!canSubmit}
      onClick={setJobContext}
      loading={isSubmit}
    >
      다음
    </LoadingButton>

    </>
  )
}